import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Download, Eye, ShieldX, Users } from 'lucide-react';
import clsx from 'clsx';
import type { QueryHistoryEntry, QueryStatus, SessionUser } from '@idas/shared';
import { api } from '../lib/api';
import { useConsoleStore } from '../store/console.store';

const STATUS_STYLES: Record<QueryStatus, string> = {
  SUCCESS: 'border-ok/30 bg-ok/10 text-ok',
  DENIED: 'border-lock/30 bg-lock/10 text-lock',
  ERROR: 'border-danger/30 bg-danger/10 text-danger',
  TIMEOUT: 'border-warn/30 bg-warn/10 text-warn',
  CANCELLED: 'border-edge bg-surface-sunken text-ink-muted',
  RUNNING: 'border-accent/30 bg-accent/10 text-accent',
  QUEUED: 'border-edge bg-surface-sunken text-ink-muted',
};

/**
 * Query history.
 *
 * An analyst sees their own runs and can reopen any of them. Auditors and
 * admins can widen the view to everyone — and doing so is itself written to
 * the audit log, because "who has been reading whose queries" is a question
 * that deserves an answer too.
 */
export function HistoryPage({ user }: { user: SessionUser }) {
  const navigate = useNavigate();
  const addTab = useConsoleStore((s) => s.addTab);
  const canSeeAll = user.permissions.includes('history:read:any');
  const [everyone, setEveryone] = useState(false);

  const history = useQuery({
    queryKey: ['history', everyone],
    queryFn: () => api.query.history({ userId: everyone ? undefined : user.id, limit: 200 }),
  });

  const reopen = (entry: QueryHistoryEntry) => {
    addTab(entry.sql);
    navigate('/console');
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-9 shrink-0 items-center gap-2 border-b border-edge bg-surface-raised px-3">
        <h1 className="text-xs font-semibold text-ink">
          {everyone ? 'All queries' : 'Your queries'}
        </h1>

        <div className="flex-1" />

        {canSeeAll && (
          <button
            className={clsx('btn', everyone && 'btn-primary')}
            onClick={() => setEveryone((v) => !v)}
            title="Viewing other people's history is recorded in the audit log."
          >
            <Users className="h-3.5 w-3.5" />
            {everyone ? 'Showing everyone' : 'Show everyone'}
          </button>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {history.isLoading && (
          <div className="space-y-2 p-3">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="h-14 animate-pulse rounded-md bg-surface-raised" />
            ))}
          </div>
        )}

        {history.data?.entries.length === 0 && (
          <p className="p-8 text-center text-2xs text-ink-faint">Nothing here yet.</p>
        )}

        <div className="divide-y divide-edge">
          {history.data?.entries.map((entry) => (
            <div key={entry.id} className="group px-3 py-2.5 hover:bg-surface-raised">
              <div className="mb-1.5 flex flex-wrap items-center gap-2">
                <span className={clsx('chip', STATUS_STYLES[entry.status])}>
                  {entry.status === 'DENIED' && <ShieldX className="h-2.5 w-2.5" />}
                  {entry.status.toLowerCase()}
                </span>

                <span className="text-2xs text-ink-faint">{entry.dataSourceName}</span>

                {entry.user && (
                  <span className="text-2xs font-medium text-ink-muted">
                    {entry.user.name}
                    {entry.user.department && (
                      <span className="font-normal text-ink-faint"> · {entry.user.department}</span>
                    )}
                  </span>
                )}

                {entry.exported && (
                  <span
                    className="chip border-warn/30 bg-warn/10 text-warn"
                    title="Results from this query were downloaded."
                  >
                    <Download className="h-2.5 w-2.5" />
                    exported
                  </span>
                )}

                <div className="flex-1" />

                {entry.rowCount !== null && (
                  <span className="text-2xs tabular-nums text-ink-faint">
                    {entry.rowCount.toLocaleString()} rows
                  </span>
                )}
                {entry.durationMs !== null && (
                  <span className="text-2xs tabular-nums text-ink-faint">
                    {entry.durationMs < 1000
                      ? `${entry.durationMs}ms`
                      : `${(entry.durationMs / 1000).toFixed(1)}s`}
                  </span>
                )}
                <span className="text-2xs text-ink-faint">
                  {new Date(entry.startedAt).toLocaleString()}
                </span>

                <button
                  className="btn btn-ghost px-1.5 opacity-0 transition-opacity group-hover:opacity-100"
                  onClick={() => reopen(entry)}
                  title="Open in a new tab"
                >
                  <Eye className="h-3 w-3" />
                </button>
              </div>

              <pre className="overflow-x-auto whitespace-pre-wrap break-words font-mono text-2xs leading-relaxed text-ink-muted">
                {entry.sql.length > 400 ? `${entry.sql.slice(0, 400)}…` : entry.sql}
              </pre>

              {entry.deniedReason && (
                <p className="mt-1.5 border-l-2 border-lock/40 pl-2 text-2xs leading-relaxed text-lock">
                  {entry.deniedReason}
                </p>
              )}
              {entry.errorMessage && (
                <p className="mt-1.5 border-l-2 border-danger/40 pl-2 text-2xs leading-relaxed text-danger">
                  {entry.errorMessage}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
